import type { ConnectionReference } from '@/types/connectionReference';
import type { AgentConnection } from '@/types/logicApps';
import type { ServiceProviderConnection } from '@/types/connections';
import {
  isAgentConnectionId,
  isHostedAgentConnectionId,
  isServiceProviderConnector,
} from './connector';
import { toBackendAgentConnectionType } from './agentConnectionType';
import { MANAGED_MODEL_CONNECTION_PROVIDER_TYPE } from './managedModelReference';

export type LocalConnectionRecord =
  | { kind: 'serviceProvider'; value: ServiceProviderConnection }
  | { kind: 'agent'; value: AgentConnection };

/**
 * Converts the workflow-facing reference returned by ConnectionCreationPanel
 * into the app-level connections.json record shape.
 */
export function connectionReferenceToLocalRecord(
  reference: ConnectionReference,
  fallbackName?: string,
): LocalConnectionRecord | null {
  if (isServiceProviderConnector(reference.api.id)) {
    const properties = reference.connectionProperties ?? {};
    const { _parameterSetName, ...parameterValues } = properties;
    const displayName = reference.connectionName ?? fallbackName;
    return {
      kind: 'serviceProvider',
      value: {
        serviceProvider: { id: reference.api.id },
        ...(displayName ? { displayName } : {}),
        ...(typeof _parameterSetName === 'string'
          ? { parameterSetName: _parameterSetName }
          : {}),
        parameterValues,
      },
    };
  }

  if (
    !isAgentConnectionId(reference.api.id) ||
    isHostedAgentConnectionId(reference.api.id)
  ) {
    return null;
  }

  const properties = reference.connectionProperties ?? {};
  const providerType =
    typeof properties.type === 'string' ? properties.type : undefined;
  if (
    providerType?.trim().toLowerCase() ===
    MANAGED_MODEL_CONNECTION_PROVIDER_TYPE.toLowerCase()
  ) {
    return null;
  }

  const modelConfiguration = properties.modelConfiguration as
    | AgentConnection['modelConfiguration']
    | undefined;
  const authentication =
    (properties.authentication as AgentConnection['authentication'] | undefined) ??
    reference.authentication;
  const displayName = reference.connectionName ?? fallbackName;

  return {
    kind: 'agent',
    value: {
      ...(displayName ? { displayName } : {}),
      modelProvider: { id: reference.api.id },
      ...(modelConfiguration ? { modelConfiguration } : {}),
      ...(typeof properties.endpoint === 'string'
        ? { endpoint: properties.endpoint }
        : {}),
      ...(typeof properties.resourceId === 'string'
        ? { resourceId: properties.resourceId }
        : {}),
      ...(typeof properties.connectionId === 'string'
        ? { connectionId: properties.connectionId }
        : {}),
      ...(providerType ? { type: toBackendAgentConnectionType(providerType) } : {}),
      ...(authentication ? { authentication } : {}),
    },
  };
}
